import React, { useEffect, useLayoutEffect, useState } from 'react';
import { debounce } from 'underscore';
import { GAME_WIDTHS, SLIDE_SIZES } from './constants';
import { useDeviceSelector, useAppSelector } from '@core/js/store/hooks';
import { compose, concat, dropLast, last, length, omit, path, pick, splitEvery } from 'ramda';
import { branch, defaultProps, mapProps, renderNothing, withHandlers, withProps, withState } from 'recompose';

export const GAMES_PER_SLIDE_MIN = 2;

export const getRightMargin = (gameRef) => {
    let marginRight = parseFloat(getComputedStyle(gameRef).marginRight);
    if (marginRight !== 0) {
        return marginRight;
    }

    // for single element in slide possible margin won't be drawn hence we couldn't be able to calculate it
    const el = document.createElement('span');
    gameRef.parentElement.appendChild(el);
    marginRight = parseFloat(getComputedStyle(gameRef).marginRight);
    gameRef.parentElement.removeChild(el);

    return marginRight;
};

export default compose(
    defaultProps({
        fillLastSlide: true,
        gamesPerSlideMin: GAMES_PER_SLIDE_MIN,
        gameWidths: GAME_WIDTHS
    }),
    // #region @theme
    withProps(() => useDeviceSelector(pick(['isDesktop', 'isTablet', 'isMobile', 'formFactor', 'isLandscape', 'isPortrait']))),
    withProps(() => ({ layouts: useAppSelector(path(['gamesTilesLayouts', 'layouts'])) })),
    withState('gamesPerSlide', 'setGamesPerSlide', ({ formFactor }) => SLIDE_SIZES[formFactor]),

    withHandlers(() => ({
        getSlideGamesAmount: ({
            gamesPerSlide,
            isDesktop,
            isTablet,
            isMobile,
            isLandscape,
            isPortrait
        }) => (dynamicSlideLength) => {
            // In case if we're inside default game feed category, we use fixed(predefined) slide length, otherwise dynamic slide length applied
            if (dynamicSlideLength) {
                return dynamicSlideLength;
            } else if (isMobile && isPortrait) {
                return SLIDE_SIZES['mobile'];
            } else if (isMobile && isLandscape || isTablet && isPortrait) {
                return SLIDE_SIZES['tablet'];
            } else if (isDesktop || isTablet && isLandscape) {
                return SLIDE_SIZES['desktop'];
            }
            return gamesPerSlide;
        }
    })),
    withHandlers(() => {
        const getTilePlacement = (tile) => {
            const yCoordinates = [];
            const xCoordinates = [];

            tile.forEach(([x, y]) => {
                xCoordinates.push(x);
                yCoordinates.push(y);
            });

            const xStart = Math.min(...xCoordinates);
            const xEnd = Math.max(...xCoordinates);
            const yStart = Math.min(...yCoordinates);
            const yEnd = Math.max(...yCoordinates);

            return xCoordinates.length && yCoordinates.length ? { width: xEnd - xStart + 1, height: yEnd - yStart + 1 } : null;
        };

        const splitTilesToSlides = (layout, gameCodes, cellsInSlide, showAll) => {
            let slide = 0;
            let currSlideLength = 0;

            return gameCodes.reduce((acc, code, idx) => {
                const tile = layout[idx] || [];
                const tileSize = tile.length || 1;
                const nextSlideLength = tileSize + currSlideLength;
                const tileSizes = getTilePlacement(tile);

                if (!acc[slide]) {
                    acc[slide] = [];
                }

                if (nextSlideLength <= cellsInSlide || tileSize > cellsInSlide || showAll) {
                    acc[slide].push({ code, size: tileSizes });
                    currSlideLength += tileSize;

                    if (!showAll && nextSlideLength === cellsInSlide) {
                        slide += 1;
                        currSlideLength = 0;
                    }
                } else if (!showAll && nextSlideLength > cellsInSlide) {
                    slide += 1;
                    currSlideLength = tileSize;

                    acc[slide] = [];
                    acc[slide].push({ code, size: tileSizes });
                }

                return acc;
            }, []);
        };

        return {
            getSlides: ({ gamesPerSlide, gameCodes, fillLastSlide }) => () => {
                // #region @core
                if (gameCodes.length === 0) {
                    return { slides: [] };
                }

                let slides = splitEvery(gamesPerSlide, gameCodes);

                if (fillLastSlide) {
                    let lastSlide = last(slides);
                    lastSlide = concat(lastSlide, Array(gamesPerSlide - length(lastSlide)).fill(null));

                    slides = concat(dropLast(1, slides), [lastSlide]);
                }
                // #endregion

                return { slides, slidesLength: slides.length }; // @theme
            },

            getLayoutSlides: ({
                layouts,
                layoutName,
                isDesktop,
                getSlideGamesAmount,
                gameCodes,
                isChildCategory
            }) => () => {
                const layoutType = isDesktop ? 'top' : 'left';
                const layout = layouts.find(curr => curr.name === layoutName)?.[layoutType];
                 // Get the Y coordinate of last item;
                const rowsAmount = Math.max(...layout.flat().map(([, y]) => y));
                const cellsInSlide = getSlideGamesAmount();
                const slides = splitTilesToSlides(layout, gameCodes, cellsInSlide * rowsAmount, !isChildCategory);

                return { slides, layout, slidesLength: slides.length };
            }
        };
    }),
    withProps(({ layouts, layoutName, getLayoutSlides, getSlides, isMobile, isDesktop, expanded, isChildCategory }) => {
        if (
            layoutName && layouts.find((curr) => curr.name === layoutName) &&
            layoutName !== '__$1X1' && (isMobile || isDesktop) && !(expanded && isChildCategory)
        ) {
            return getLayoutSlides();
        }

        return getSlides();
    }),
    // #endregion
    branch(
        props => !props.slides.length,
        renderNothing
    ),
    withHandlers(({
        setGamesPerSlide,
        setSlidesLength,
        isDesktop,
        gameSize,
        gamesPerSlideMin,
        gameWidths,
        // #region @theme
        getSlideGamesAmount,
        isGameFeed
        // #endregion
    }) => {
        let containerRef;
        let gameRef;
        let frame;

        const setSlidesLengthInternal = ({ gamesPerSlide, gameCodes }) => {
            setSlidesLength && setSlidesLength(gameCodes.length / gamesPerSlide); // @theme
        };

        const getGamesPerSlide = ({ gamesPerSlide, gameCodes }) => {
            const formFactor = isDesktop ? 'DESKTOP' : 'MOBILE';
            const minGameWidth = gameWidths[gameSize][formFactor];

            if (containerRef && gameRef) {
                const { floor, max } = Math;
                const { offsetWidth } = containerRef;
                const { paddingLeft, paddingRight } = getComputedStyle(containerRef);
                const gameMarginRight = getRightMargin(gameRef);
                const containerWidth = offsetWidth - parseFloat(paddingLeft) - parseFloat(paddingRight)
                    + gameMarginRight; // last game goes without margin
                const minTotalGameWidth = minGameWidth + gameMarginRight;
                const gamesCount = floor(containerWidth / minTotalGameWidth);
                // #region @theme
                const maxGamesCount = max(gamesCount, gamesPerSlideMin);
                const totalGamesCount = getSlideGamesAmount(isGameFeed ? null : maxGamesCount);
                // #endregion

                if (gamesPerSlide !== totalGamesCount) {
                    setGamesPerSlide(totalGamesCount);
                    setSlidesLengthInternal({ gamesPerSlide: totalGamesCount, gameCodes });
                }
            }
        };

        return {
            setSlideRef: props => (ref) => {
                containerRef = ref;
                getGamesPerSlide(props);
            },
            setGameRef: props => (ref) => {
                gameRef = ref;
                getGamesPerSlide(props);
            },
            getGamesPerSlide: props => () => getGamesPerSlide(props),
            throttledGetGamePerSlide: props => () => {
                cancelAnimationFrame(frame);
                frame = requestAnimationFrame(() => getGamesPerSlide(props));
            },
            setSlidesLength: ({ gamesPerSlide, gameCodes }) =>
                () => setSlidesLengthInternal({ gamesPerSlide, gameCodes })
        };
    }),
    Wrapped => (props) => {
        const [slideContainer, setSlideContainer] = useState(null);
        const {
            throttledGetGamePerSlide,
            expanded,
            isVisible,
            getGamesPerSlide,
            setSlidesLength,
            gameCodes,
            setSlideRef,
            slidesLength // @theme
        } = props;
        const { os, browserName } = useDeviceSelector(pick(['os', 'browserName']));
        const debouncedGetGamesPerSlide = debounce(() => {
            os === 'ios' && browserName === 'chrome' && getGamesPerSlide();
        }, 300);

        const handleSetSlideRef = (ref) => {
            setSlideContainer(ref);

            if (typeof setSlideRef === 'function') {
                setSlideRef(ref);
            }
        };

        useEffect(() => {
            const debouncedResizeAndScrollY = debounce(() => {
                throttledGetGamePerSlide();
                window.scrollTo(0, window.scrollY);
            }, 300);
            const resizeObserver = new ResizeObserver(() => {
                debouncedResizeAndScrollY();
            });

            slideContainer && resizeObserver.observe(slideContainer);

            return () => {
                slideContainer && resizeObserver.unobserve(slideContainer);
            };
        }, [slideContainer, throttledGetGamePerSlide]);

        useEffect(() => {
            window.addEventListener('orientationchange', debouncedGetGamesPerSlide);

            return () => {
                window.removeEventListener('orientationchange', debouncedGetGamesPerSlide);
            };
        }, [debouncedGetGamesPerSlide]);

        useLayoutEffect(getGamesPerSlide, [expanded, isVisible, getGamesPerSlide]);
        useLayoutEffect(setSlidesLength, [gameCodes, setSlidesLength]);

        // #region @theme
        useEffect(() => {
            setSlidesLength(slidesLength);
        }, [slidesLength, setSlidesLength]);
        // #endregion

        return <Wrapped {...props} setSlideRef={handleSetSlideRef} />;
    },
    mapProps(omit([
        'fillLastSlide',
        'gamesPerSlideMin',
        'isDesktop',
        // @theme removed
        'setGamesPerSlide',
        'getGamesPerSlide',
        'throttledGetGamePerSlide',
        // @theme removed
        // #region @theme
        'getSlides',
        'setSlidesLength',
        'getLayoutSlides',
        'getSlideGamesAmount'
        // #endregion
    ]))
);
